/**
 * Le flow, au bout de la rangée d'icônes du composeur — et « À faire
 * maintenant » au-dessus du champ.
 *
 * Copie la forme du poste (`panels/fan/ConvStatusBar`, mode `enPoste`) :
 *   · rien ne tourne → « Flow terminé · script 1 » s'il y en a eu un, puis le
 *     bouton « Flow ▾ » qui ouvre « Lancer un flow » (`FlowPickMenu`) — un flow
 *     indisponible y est GRISÉ avec sa raison, jamais caché ;
 *   · un flow tourne → son badge (le VRAI `FlowRunBadge`, pur), le carré rouge
 *     qui l'arrête, et le prochain message avec « Envoyer maintenant »
 *     (`ProchainMessageDuFlow`, forme compacte).
 *
 * « À faire maintenant » copie `ScriptSuggestionBar` : un cadre plein, FERMÉ
 * d'office (Kayla, 17-09 : « qu'ils aient à cliquer pour l'ouvrir »), dont la
 * ligne de titre dit ce qu'il y a dedans (« Voir le flow », « 1 message »).
 *
 * ⛔ Le vrai `FlowPickMenu` lit les flows du serveur ; le vrai lanceur les
 * démarre pour de vrai. Ici, tout passe par le moteur du test.
 *
 * Le menu « Lancer un flow » est le `useMenu` de la librairie : porté sur
 * <body>, il se ferme tout seul (clic dehors, Échap), et un flow grisé y garde sa
 * raison en sous-texte.
 */
import { useState } from "react";

import { FlowRunBadge } from "@/shared/ui/FlowRunBadge";
import { Button, Carte, Etiquette, Infobulle, cn, useMenu, type EntreeMenu } from "@/shared/ui";
import { ChevronDown, ChevronUp, Sparkles, Square, Workflow } from "@/shared/ui/icons";

import type { SuggestionAFaire } from "./conseils";
import { motifIndisponible, prochainMessageDuFlow, type Etat } from "./moteur";
import { FLOWS, flowParCle } from "./scenario";

interface PropsBarre {
  etat: Etat;
  fanId: string;
  onLancer: (flowCle: string) => void;
  onArreter: () => void;
  onEnvoyerMaintenant: () => void;
}

export function BarreDuFlow({ etat, fanId, onLancer, onArreter, onEnvoyerMaintenant }: PropsBarre) {
  const { ouvrirSous, ouvert, menu } = useMenu();

  const run = etat.run && etat.run.fanId === fanId ? etat.run : null;
  const flowDuRun = run ? flowParCle(run.flowCle) : null;

  if (run && run.statut === "en_cours" && flowDuRun) {
    const prochain = prochainMessageDuFlow(etat);
    return (
      <div className="flex min-w-0 flex-wrap items-center justify-end gap-2" data-testid="flow-en-cours">
        <FlowRunBadge
          size="sm"
          className="h-9"
          flowName={flowDuRun.nom}
          completed={run.envoyes}
          total={run.total}
          percent={(run.envoyes / Math.max(1, run.total)) * 100}
        />
        <Infobulle texte="Arrêter le flow">
          <Button
            variant="destructive"
            size="lg"
            iconOnly
            onClick={onArreter}
            aria-label="Arrêter le flow"
            className="h-9 w-9 shrink-0"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </Button>
        </Infobulle>
        {prochain && (
          <span className="flex min-w-0 items-center gap-2 text-[13px] text-muted">
            <Infobulle texte={prochain}>
              <span className="min-w-0 max-w-[14rem] truncate">Ensuite : « {prochain} »</span>
            </Infobulle>
            <Button variant="secondary" size="lg" className="min-h-9 shrink-0" onClick={onEnvoyerMaintenant}>
              Envoyer maintenant
            </Button>
          </span>
        )}
      </div>
    );
  }

  /** Les flows du menu — un flow indisponible est GRISÉ, avec sa raison dessous. */
  const entrees = (): EntreeMenu[] => [
    { titre: "Lancer un flow" },
    ...FLOWS.map((f) => {
      const motif = motifIndisponible(etat, fanId, f.cle);
      const etapes = f.pas.filter((p) => p.de === "flow").length;
      return {
        label: f.nom,
        sous: motif ?? undefined,
        raccourci: `${etapes} étapes`,
        icone: <Workflow aria-hidden />,
        desactive: motif !== null,
        onClick: () => onLancer(f.cle),
      };
    }),
  ];

  return (
    <div className="flex min-w-0 items-center gap-2">
      {run && flowDuRun && (
        <Infobulle texte={flowDuRun.nom}>
          <span className="flex h-10 min-w-0 items-center gap-1.5 text-[13px] text-muted">
            <Workflow className="h-4 w-4 shrink-0 text-faint" aria-hidden />
            <span className="truncate">
              {run.statut === "termine" ? `Flow terminé · ${flowDuRun.nom}` : `Flow arrêté · ${flowDuRun.nom}`}
            </span>
          </span>
        </Infobulle>
      )}
      <Infobulle texte="Lancer un flow sur cette conversation">
        <Button
          variant="ghost"
          size="lg"
          icon={<Workflow className="h-4 w-4 shrink-0" aria-hidden />}
          iconRight={<ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />}
          aria-haspopup="menu"
          aria-expanded={ouvert}
          aria-label="Lancer un flow"
          className="min-h-10 shrink-0 px-3 text-[13px]"
          onClick={(e) => ouvrirSous(e.currentTarget, entrees(), { alignement: "droite" })}
        >
          Flow
        </Button>
      </Infobulle>
      {menu}
    </div>
  );
}

interface PropsAFaire {
  suggestion: Exclude<SuggestionAFaire, null>;
  onLancer: (flowCle: string) => void;
  onInserer: (texte: string) => void;
}

/** « À faire maintenant » — fermé d'office, refermé quand la consigne change. */
export function AFaireMaintenant({ suggestion, onLancer, onInserer }: PropsAFaire) {
  const [ouvert, setOuvert] = useState(false);
  const titreDroite = ouvert
    ? "Fermer"
    : suggestion.genre === "flow"
      ? "Voir le flow"
      : `${suggestion.bulles.length} message${suggestion.bulles.length > 1 ? "s" : ""}`;

  return (
    <Carte remplissage="sm" role="region" aria-label="À faire maintenant" data-testid="a-faire-maintenant">
      <Infobulle texte={ouvert ? "Fermer le volet" : "Voir les messages"}>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => setOuvert((v) => !v)}
          aria-expanded={ouvert}
          className={cn("h-auto min-h-11 w-full flex-wrap justify-start gap-x-2.5 gap-y-1 whitespace-normal px-3 py-2 text-left", ouvert && "mb-2")}
        >
          <Etiquette icone={<Sparkles aria-hidden />}>À faire maintenant</Etiquette>
          {suggestion.genre === "bulles" && (
            <span className="min-w-0 break-words text-[14px] font-semibold text-fg">· {suggestion.motif}</span>
          )}
          <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[13px] font-normal text-muted">
            {titreDroite}
            {ouvert ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
          </span>
        </Button>
      </Infobulle>

      {ouvert && suggestion.genre === "flow" && (
        <div>
          <Button
            variant="accent"
            size="lg"
            className="min-h-12 w-full justify-start gap-2.5 text-[15px]"
            onClick={() => onLancer(suggestion.flowCle)}
          >
            <Workflow className="h-5 w-5 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-left">Lancer le flow « {suggestion.flowNom} »</span>
          </Button>
          <p className="mt-2 text-[13px] leading-snug text-muted">{suggestion.motif}</p>
        </div>
      )}

      {ouvert && suggestion.genre === "bulles" && (
        <div className="flex flex-col gap-2">
          {suggestion.bulles.map((b) => (
            <Carte
              key={b}
              remplissage="sm"
              onClick={() => onInserer(b)}
              className="min-h-11 whitespace-pre-wrap text-[14px] leading-snug"
            >
              {b}
            </Carte>
          ))}
          <p className="text-[13px] leading-snug text-muted">
            Touche une bulle : elle se met dans ta zone d'écriture. Tu relis, tu envoies.
          </p>
        </div>
      )}
    </Carte>
  );
}
